import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";

// FleetSafetyPage — the fleet safety-ops view:
//   1. Version matrix: per-device applied version vs published, for every
//      catalog kind (rule_pack / slot_policy / cartridge / detector_weights),
//      with a stale flag when a device hasn't reported inside the window.
//   2. Safety-issue feed: fleet-wide safety events (gate blocks, silicon
//      ratchet rejects, apply failures) newest first.
//
// Data: GET /api/v1/fleet-matrix + GET /api/v1/safety-events (operator+).
// HONESTY: rows come from host-reported telemetry over a bearer token — they
// are operational visibility, not silicon-attested facts.

interface MatrixRow {
  device_id: string;
  cohort?: string;
  enrolled: boolean;
  last_seen?: string;
  stale: boolean;
  applied?: Record<string, number>;
  applied_at?: string;
  behind?: string[];
  counters?: Record<string, number>;
  counters_at?: string;
  safety_events_total: number;
  anomalies_reported: number;
}

interface MatrixResponse {
  kinds: string[];
  published: Record<string, number>;
  stale_after_s: number;
  devices: MatrixRow[];
  generated_at: string;
}

interface FeedEvent {
  ts: string;
  source: string;
  kind: string;
  severity?: string;
  value?: number;
  slot?: number;
  detail?: string;
  device_id: string;
  received_at: string;
}

interface FeedResponse {
  items: FeedEvent[];
  ring_total: number;
}

const KIND_LABELS: Record<string, string> = {
  rule_pack: "Rule pack",
  slot_policy: "Slot policy",
  cartridge: "Cartridge",
  detector_weights: "Detector weights",
};

export function FleetSafetyPage() {
  const [severity, setSeverity] = useState<string>("");

  const matrix = useQuery<MatrixResponse>({
    queryKey: ["fleet-matrix"],
    queryFn: () => api<MatrixResponse>("/api/v1/fleet-matrix"),
    refetchInterval: 30_000,
  });

  const feed = useQuery<FeedResponse>({
    queryKey: ["safety-events", severity],
    queryFn: () =>
      api<FeedResponse>(
        "/api/v1/safety-events?limit=100" + (severity ? `&severity=${severity}` : ""),
      ),
    refetchInterval: 30_000,
  });

  const kinds = matrix.data?.kinds ?? [];
  const published = matrix.data?.published ?? {};
  const rows = [...(matrix.data?.devices ?? [])].sort((a, b) =>
    a.device_id.localeCompare(b.device_id),
  );

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h1 className="text-lg font-semibold">Fleet safety — version matrix</h1>
          {matrix.data && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              stale after {Math.round(matrix.data.stale_after_s / 60)} min without telemetry
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Applied versions are host-reported (bearer-authenticated, not
          silicon-attested). Red cells are behind the published catalog.
        </p>

        {matrix.isLoading && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
        {matrix.isError && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">
            Failed to load fleet matrix.
          </p>
        )}

        {matrix.data && (
          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-800 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Device</th>
                  <th className="px-3 py-2 font-medium">Cohort</th>
                  <th className="px-3 py-2 font-medium">Last seen</th>
                  {kinds.map((k) => (
                    <th key={k} className="px-3 py-2 font-medium whitespace-nowrap">
                      {KIND_LABELS[k] ?? k}
                      <span className="ml-1 font-normal text-slate-500 dark:text-slate-400">
                        (pub v{published[k] ?? 0})
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-2 font-medium">Blocks</th>
                  <th className="px-3 py-2 font-medium">Events</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5 + kinds.length} className="px-3 py-6 text-center text-slate-500">
                      No devices yet — enrol a device and start its update agent.
                    </td>
                  </tr>
                )}
                {rows.map((d) => (
                  <tr key={d.device_id}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {d.device_id}
                      {!d.enrolled && (
                        <span className="ml-2 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 text-xs">
                          unenrolled
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{d.cohort || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={d.stale ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}>
                        {d.last_seen || "never"}
                      </span>
                      {d.stale && (
                        <span className="ml-2 rounded bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-1.5 py-0.5 text-xs">
                          stale
                        </span>
                      )}
                    </td>
                    {kinds.map((k) => {
                      const applied = d.applied?.[k];
                      const behind = d.behind?.includes(k);
                      return (
                        <td key={k} className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={
                              "rounded px-1.5 py-0.5 font-mono text-xs " +
                              (behind
                                ? "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300"
                                : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300")
                            }
                            title={behind ? `behind published v${published[k]}` : "current"}
                          >
                            {applied !== undefined ? `v${applied}` : "—"}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 font-mono">
                      {d.counters?.["gate_blocks_total"] ?? "—"}
                    </td>
                    <td className="px-3 py-2 font-mono">{d.safety_events_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold">Safety-issue feed</h2>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
          >
            <option value="">all severities</option>
            <option value="critical">critical</option>
            <option value="warn">warn</option>
            <option value="info">info</option>
          </select>
        </div>

        {feed.isLoading && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
        {feed.data && feed.data.items.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">
            No safety events reported{severity ? ` at severity "${severity}"` : ""} (ring
            holds the last {feed.data.ring_total} since server start; durable log lives
            in SA_DATA_DIR/safety_events/).
          </p>
        )}
        {feed.data && feed.data.items.length > 0 && (
          <ul className="mt-4 space-y-2">
            {feed.data.items.map((e, i) => (
              <li
                key={`${e.device_id}-${e.ts}-${i}`}
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
              >
                <SeverityChip severity={e.severity} />
                <span className="font-mono text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {e.ts}
                </span>
                <span className="font-mono">{e.device_id}</span>
                <span className="text-slate-600 dark:text-slate-300">
                  {e.source}/{e.kind}
                  {e.slot !== undefined && e.slot !== null ? ` (slot ${e.slot})` : ""}
                </span>
                {e.value !== undefined && e.value !== 0 && (
                  <span className="font-mono text-xs rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5">
                    ×{e.value}
                  </span>
                )}
                {e.detail && (
                  <span className="text-slate-500 dark:text-slate-400 truncate max-w-md">
                    {e.detail}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SeverityChip({ severity }: { severity?: string }) {
  const s = severity || "info";
  const cls =
    s === "critical"
      ? "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300"
      : s === "warn"
        ? "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300"
        : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300";
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs uppercase tracking-wide ${cls}`}>
      {s}
    </span>
  );
}
